import CreateTimeRegistrationMutation from '../../../../../../../mutations/create_time_registration_mutation.modern.js';
import UpdateTimeRegistrationMutation from '../../../../../../../mutations/update_time_registration_mutation.modern.js';
import {updateTaskForScoping} from '../../../../../../project-tab/projects/scoping-page/ProjectScopingUtil';
import {MODAL_TYPE, showModal} from '../../../../../components/modals/generic_modal_conductor';
import {createToast} from '../../../../../components/toasts/another-toast/toaster';
import Util from '../../../../../util/util';
import {TimeRegistrationEntity} from '../types/TimeRegistrationEntityId';
import {isTargetEntityBillable} from '../util/TimeRegistrationModalUtil';
import {getBillableMinutesForMutation} from '../../../../../util/time-registration/time-registration-settings/BillableTimeSplitUtil';

import type {TimeRegistrationModal_entity} from '../types/TimeRegistrationModalEntity';

type TimeRegistrationMutationTaskInput = {
	taskId: string;
	projectId: string | null;
};

type TimeRegistrationMutationProjectInput = {
	projectId: string;
};

type TimeRegistrationMutationIdleTimeInput = {
	idleTimeId: string;
};

type TimeRegistrationMutationIdInput =
	| TimeRegistrationMutationTaskInput
	| TimeRegistrationMutationProjectInput
	| TimeRegistrationMutationIdleTimeInput;

function getMutationIdFromTargetEntity(targetEntity: TimeRegistrationModal_entity): TimeRegistrationMutationIdInput {
	switch (targetEntity.entity) {
		case TimeRegistrationEntity.Task:
			return {taskId: targetEntity.id, projectId: targetEntity.project ? targetEntity.project.id : null};
		case TimeRegistrationEntity.Project:
			return {projectId: targetEntity.id};
		case TimeRegistrationEntity.IdleTime:
			return {idleTimeId: targetEntity.id};
	}
}

type HandleMutationResultInput = {
	result;
	taskId: string | null;
	onSuccess?: () => void;
};
function handleMutationResult({result, taskId, onSuccess}: HandleMutationResultInput) {
	const resultType = result.createTimeReg || result.updateTimeReg;
	if (
		resultType &&
		resultType.errors &&
		resultType.errors.length === 1 &&
		resultType.errors[0] === 'TIME_REGISTRATION_IN_LOCKED_PERIOD'
	) {
		showModal({
			type: MODAL_TYPE.WARNING_DATE_LOCKED,
		});
	} else if (Util.checkForSageErrorAndShowModal(resultType.errors, true)) {
		return;
	} else {
		updateTaskForScoping(taskId, onSuccess);
	}
}

export type TimeRegistrationMutationInput = {
	personId: string;
	companyId: string;
	targetEntity: TimeRegistrationModal_entity;
	time: number;
	billableTime?: number | null;
	date: Date;
	note: string;
	harvestTaskId?: string;
	unit4ActivityId?: string;
	unit4ActivityName?: string;
	roleId?: string;
	onCreateSuccessCallbacks?: (() => void)[];
};

export function createTimeRegistration({
	companyId,
	targetEntity,
	time,
	billableTime,
	date,
	personId,
	note,
	harvestTaskId,
	unit4ActivityId,
	unit4ActivityName,
	roleId,
	onCreateSuccessCallbacks,
}: TimeRegistrationMutationInput): void {
	const mutationTargetId = getMutationIdFromTargetEntity(targetEntity);
	const onSuccess = () => {
		createToast({
			duration: 5000,
			message: 'Time Registration created',
		});
		if (onCreateSuccessCallbacks) {
			for (let i = 0; i < onCreateSuccessCallbacks.length; i++) {
				onCreateSuccessCallbacks[i]();
			}
		}
	};
	const handleResult = result => {
		handleMutationResult({
			result,
			taskId: 'taskId' in mutationTargetId ? mutationTargetId.taskId : null,
			onSuccess,
		});
	};

	Util.CommitSchedulingModalUpdate(
		CreateTimeRegistrationMutation,
		{
			...mutationTargetId,
			companyId: companyId,
			source: 'Time-registration-modal',
			minutesRegistered: time,
			billableMinutesRegistered: getBillableMinutesForMutation(isTargetEntityBillable(targetEntity), billableTime),
			day: date.getDate(),
			month: date.getMonth() + 1,
			year: date.getFullYear(),
			personId: personId,
			notes: note !== '' ? note : null,
			harvestTaskId,
			unit4ActivityId,
			unit4ActivityName,
			roleId,
		},
		handleResult
	);
}

export interface UpdateTimeRegistrationMutationInput extends TimeRegistrationMutationInput {
	timeRegistrationId: string;
}
export function updateTimeRegistration({
	timeRegistrationId,
	targetEntity,
	time,
	billableTime,
	date,
	note,
	harvestTaskId,
	unit4ActivityId,
	unit4ActivityName,
	roleId,
}: UpdateTimeRegistrationMutationInput): void {
	const mutationTargetId = getMutationIdFromTargetEntity(targetEntity);
	const onSuccess = () => {
		createToast({
			duration: 5000,
			message: 'Time Registration updated',
		});
	};
	const handleResult = result =>
		handleMutationResult({
			result,
			taskId: 'taskId' in mutationTargetId ? mutationTargetId.taskId : null,
			onSuccess,
		});

	Util.CommitSchedulingModalUpdate(
		UpdateTimeRegistrationMutation,
		{
			...mutationTargetId,
			id: timeRegistrationId,
			source: 'Time-registration-modal',
			minutesRegistered: time,
			billableMinutesRegistered: getBillableMinutesForMutation(isTargetEntityBillable(targetEntity), billableTime),
			day: date.getDate(),
			month: date.getMonth() + 1,
			year: date.getFullYear(),
			updateTimeRegLocation: true,
			notes: note !== '' ? note : null,
			harvestTaskId,
			unit4ActivityId,
			unit4ActivityName,
			roleId,
		},
		handleResult
	);
}
